import React, { useEffect, useRef, useState } from "react";
import TopNav from "@/components/TopNav";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UploadCloud, Download, Trash2, Lock, FileText } from "lucide-react";
import { api, API } from "@/lib/api";
import { VAULT } from "@/constants/testIds";

const CATEGORIES = ["general", "certification", "license", "id", "contract", "resume"];

export default function DocumentVault() {
    const [docs, setDocs] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [category, setCategory] = useState("general");
    const fileRef = useRef(null);

    async function load() {
        try {
            const { data } = await api.get("/vault/list");
            setDocs(data);
        } catch { /* noop */ }
    }
    useEffect(() => { load(); }, []);

    async function upload(e) {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            fd.append("category", category);
            await api.post("/vault/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Uploaded & encrypted");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    }

    async function download(id) {
        try {
            const { data } = await api.get(`/vault/signed-url/${id}`);
            window.open(`${API}/vault/download?token=${encodeURIComponent(data.token)}`, "_blank");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to get signed URL");
        }
    }

    async function remove(id) {
        if (!window.confirm("Delete this document?")) return;
        try {
            await api.delete(`/vault/${id}`);
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    }

    return (
        <div className="min-h-screen bg-stone-50">
            <TopNav />
            <main data-testid={VAULT.root} className="max-w-6xl mx-auto px-6 lg:px-8 py-10 animate-fade-in">
                <div className="mb-8">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-2">Secure vault</div>
                    <h1 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900 flex items-center gap-3">
                        <Lock className="w-7 h-7 text-emerald-700" strokeWidth={1.75} />
                        Encrypted document vault
                    </h1>
                    <p className="text-sm text-stone-500 mt-2 font-dmsans">
                        Files are encrypted at rest with Fernet. Downloads use 5-minute signed URLs. Per-user ACL enforced server-side.
                    </p>
                </div>

                <Card className="rounded-2xl border-stone-200 mb-6">
                    <CardContent className="p-6">
                        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
                            <div className="flex-1">
                                <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">Category</div>
                                <Select value={category} onValueChange={setCategory}>
                                    <SelectTrigger data-testid={VAULT.categorySelect} className="h-11 rounded-xl">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CATEGORIES.map((c) => (
                                            <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <input
                                data-testid={VAULT.uploadInput}
                                ref={fileRef}
                                type="file"
                                onChange={upload}
                                disabled={uploading}
                                className="hidden"
                                id="vault-upload"
                            />
                            <Button
                                data-testid={VAULT.uploadBtn}
                                onClick={() => fileRef.current?.click()}
                                disabled={uploading}
                                className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white h-11 px-6"
                            >
                                <UploadCloud className="w-4 h-4 mr-2" /> {uploading ? "Encrypting..." : "Upload document"}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                <Card className="rounded-2xl border-stone-200">
                    <CardContent className="p-0 overflow-hidden">
                        <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-stone-200 bg-stone-50 text-xs font-bold uppercase tracking-[0.2em] text-stone-500">
                            <div className="col-span-5">File</div>
                            <div className="col-span-2">Category</div>
                            <div className="col-span-2">Size</div>
                            <div className="col-span-2">Uploaded</div>
                            <div className="col-span-1 text-right">Actions</div>
                        </div>
                        {docs.length === 0 && (
                            <div className="p-10 text-center text-sm text-stone-500 font-dmsans">
                                No documents yet. Upload your first file.
                            </div>
                        )}
                        {docs.map((d) => (
                            <div
                                key={d.id}
                                data-testid={VAULT.row}
                                className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-stone-100 last:border-0 items-center hover:bg-stone-50 transition-colors"
                            >
                                <div className="col-span-5 flex items-center gap-3 min-w-0">
                                    <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                                        <FileText className="w-4 h-4 text-emerald-700" strokeWidth={1.75} />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-medium text-stone-900 truncate">{d.filename}</div>
                                        <div className="text-xs text-stone-400 flex items-center gap-1 font-dmsans">
                                            <Lock className="w-3 h-3" /> Encrypted
                                        </div>
                                    </div>
                                </div>
                                <div className="col-span-2 text-sm text-stone-600 font-dmsans capitalize">{d.category}</div>
                                <div className="col-span-2 text-sm text-stone-600 font-dmsans">
                                    {(d.size_bytes / 1024).toFixed(1)} KB
                                </div>
                                <div className="col-span-2 text-sm text-stone-500 font-dmsans">
                                    {new Date(d.created_at).toLocaleDateString()}
                                </div>
                                <div className="col-span-1 flex justify-end gap-1">
                                    <Button
                                        data-testid={VAULT.downloadBtn}
                                        size="icon"
                                        variant="ghost"
                                        onClick={() => download(d.id)}
                                        className="h-9 w-9 rounded-full"
                                    >
                                        <Download className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        data-testid={VAULT.deleteBtn}
                                        size="icon"
                                        variant="ghost"
                                        onClick={() => remove(d.id)}
                                        className="h-9 w-9 rounded-full text-red-600 hover:bg-red-50 hover:text-red-700"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            </main>
        </div>
    );
}
